import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as dayjs from 'dayjs';

// Types
import HostTarget from "../../types/host/HostTarget";
import InstanceTarget from "../../types/instance/InstanceTarget";
import HostStatistic from "../../types/host/HostStatistic";
import CombinedChanges from "../../types/instance/CombinedChanges";
import ChartPlotLine from "../../types/ChartPlotLine";

// Components
import WidgetCard from "../../component/instance/widget";
import ProcessorChart from "../../component/host/chart/ProcessorChart";
import MemoryChart from "../../component/host/chart/MemoryChart";
import DiskChart from "../../component/host/chart/DiskChart";
import DiskUtilisationChart from "../../component/host/chart/DiskUtilisationChart";
import HostsTabbed from "./HostsTabbed";

// Helpers
import Helper from "../../helpers/Helper";

// API
import EventsApi from "../../api/instance/EventsApi";

// Constants
import { DATA_LOADED, DATA_LOADING } from "../../component/Constants";

// Contexts
import { TimeRangeContext } from "../../context/TimeRangeContext";
import api from "../../api/Base";
import { showError } from "../../utils/showAlert";

interface IListProps {
    instances: InstanceTarget[],
    hosts: HostTarget[],
    toggleMenu: Function,
    applyPeriod: Function
}

const Detail: React.FC<IListProps> = (props) => {
    const [loading, setLoading] = useState<number>(DATA_LOADING)

    const [averageProcessorMetric, setAverageProcessorMetric] = useState<number>(0)
    const [previousAverageProcessorMetric, setPreviousAverageProcessorMetric] = useState<number | null>(0)
    const [averageMemoryMetric, setAverageMemoryMetric] = useState<number>(0)
    const [previousAverageMemoryMetric, setPreviousAverageMemoryMetric] = useState<number | null>(0)
    const [averageDiskMetric, setAverageDiskMetric] = useState<number | null>(0)
    const [previousAverageDiskMetric, setPreviousAverageDiskMetric] = useState<number | null>(0)

    const [chartPlotLines, setChartPlotLines] = useState<ChartPlotLine[]>([])
    
    const timeRangeContext = useContext(TimeRangeContext)
    const navigate = useNavigate();
    const { hostId } = useParams();
    const { hosts } = props;
    // Get the selected host.
    const filteredHosts: HostTarget[] = hosts.filter(host => Number(host.id) === Number(hostId));
    const host = filteredHosts[0]

    const comparisonSupported = useMemo(() => {
        return Helper.isComparisonSupported(timeRangeContext)
    }, [timeRangeContext]);

    const getData = async() => {
        // Check if host exists, if not redirect to not-found
        if (filteredHosts.length === 0) {
            navigate('/not-found');
            return;
        }

        const promiseArray = [
            getProcessorAverage(filteredHosts[0]), 
            getMemoryAverage(filteredHosts[0]), 
            getDiskAverage(filteredHosts[0]),
            getChanges(filteredHosts[0])
        ]

        await Promise.all(promiseArray)
        setLoading(DATA_LOADED)
    }

    useEffect(() => {
        setLoading(DATA_LOADING)
        void getData();

    }, [hostId, timeRangeContext]);

    const getProcessorAverage = async(host: HostTarget) => {
        let averageProcessorMetric: number = 0,
            previousAverageProcessorMetric: number | null = null;

        // Get current processor average.
        await api.get(`host/statistic/summary?${timeRangeContext.getTimeRangeQueryString()}&statistic=cpuutilisation&id=${host.id}`)
            .then((response: { data: HostStatistic[]; }) => {
                if (response.data.length > 0) {
                    averageProcessorMetric = Math.round(response.data[0].avg);
                }
            })
            .catch((error: any) => {
                console.error('Failed to retrieve processor average.', error);
                showError('Failed to retrieve processor average', error.message || error.toString());
            });

        if (comparisonSupported) {
            // Get previous processor average.
            await api.get(`host/statistic/summary?${timeRangeContext.getTimeRangeQueryString(true)}&statistic=cpuutilisation&id=${host.id}`)
                .then((response: { data: HostStatistic[]; }) => {
                    if (response.data.length > 0) {
                        previousAverageProcessorMetric = Math.round(response.data[0].avg);
                    }
                })
                .catch((error: any) => {
                    console.error('Failed to retrieve previous processor average.', error);
                    showError('Failed to retrieve previous processor average', error.message || error.toString());
                });
        }
        setAverageProcessorMetric(averageProcessorMetric)
        setPreviousAverageProcessorMetric(previousAverageProcessorMetric)
    }

    const getMemoryAverage = async(host: HostTarget) => {
        let averageMemoryMetric: number = 0,
            previousAverageMemoryMetric: number | null = null;

        // Get current processor average.
        await api.get(`host/statistic/summary?${timeRangeContext.getTimeRangeQueryString()}&statistic=memoryutilisation&id=${host.id}`)
            .then((response: { data: HostStatistic[]; }) => {
                if (response.data.length > 0) {
                    averageMemoryMetric = Math.round(response.data[0].avg);
                }
            })
            .catch((error: any) => {
                console.error('Failed to retrieve memory average.', error);
                showError('Failed to retrieve memory average', error.message || error.toString());
            });

        if (comparisonSupported) {
            // Get previous processor average.
            await api.get(`host/statistic/summary?${timeRangeContext.getTimeRangeQueryString(true)}&statistic=memoryutilisation&id=${host.id}`)
                .then((response: { data: HostStatistic[]; }) => {
                    if (response.data.length > 0) {
                        previousAverageMemoryMetric = Math.round(response.data[0].avg);
                    }
                })
                .catch((error: any) => {
                    console.error('Failed to retrieve previous memory average.', error);
                    showError('Failed to retrieve previous memory average', error.message || error.toString());
                });
        }

        setAverageMemoryMetric(averageMemoryMetric)
        setPreviousAverageMemoryMetric(previousAverageMemoryMetric)
    }

    const getDiskAverage = async(host: HostTarget) => {

        let metric: number = 0,
            previousMetric: number | null = null;

        // Get current processor average.
        await api.get(`host/statistic/summary?${timeRangeContext.getTimeRangeQueryString()}&statistic=ioutilisation&id=${host.id}`)
            .then((response: { data: HostStatistic[]; }) => {
                if (response.data.length > 0) {
                    metric = Math.round(response.data[0].avg);
                }
            })
            .catch((error: any) => {
                console.error('Failed to retrieve disk average.', error);
                showError('Failed to retrieve disk average', error.message || error.toString());
            });

        if (comparisonSupported) {
            // Get previous processor average.
            await api.get(`host/statistic/summary?${timeRangeContext.getTimeRangeQueryString(true)}&statistic=ioutilisation&id=${host.id}`)
                .then((response: { data: HostStatistic[]; }) => {
                    if (response.data.length > 0) {
                        previousMetric = Math.round(response.data[0].avg);
                    }
                })
                .catch((error: any) => {
                    console.error('Failed to retrieve previous disk average.', error);
                    showError('Failed to retrieve previous disk average', error.message || error.toString());
                });
        }
        setAverageDiskMetric(metric)
        setPreviousAverageDiskMetric(previousMetric)
    }

    const getChanges = async(host: HostTarget) => {
        const allEvents = await EventsApi.getEventsChangesAndAlertsForHost(timeRangeContext, Number(host.id));

        let combinedChanges: CombinedChanges[] = [],
            plotLines: ChartPlotLine[] = [];

        for (let index = 0; index < allEvents.length; index++) {
            const currentItem = allEvents[index];
            combinedChanges.push({
                id: index,
                instanceId: currentItem.datasourceid,
                hostId: currentItem.hostid,
                timeslice: currentItem.timeslice,
                [currentItem.event]: currentItem
            });
        }

        // Set the sort order for the combined changes and events.
        combinedChanges.sort(function compare(a, b) {
            let aa: number = dayjs.default(a.timeslice).valueOf();
            let bb: number = dayjs.default(b.timeslice).valueOf();
            return bb - aa;
        });

        const interval = Helper.getInterval(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, timeRangeContext.timeRange?.interval)
        const chartCategories = Helper.getChartAxisCategories(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, interval)
        plotLines = Helper.getChartPlotLines(timeRangeContext.getTimeRangeRedirectString(), combinedChanges, chartCategories, interval);

        setChartPlotLines(plotLines);
    }

    const refresh = () => {
        setLoading(DATA_LOADING)
        void getData();
    }

    // If host is not found, return null (will redirect via useEffect)
    if (!host) {
        return null;
    }

    return (
        <HostsTabbed
            hosts={props.hosts}
            toggleMenu={props.toggleMenu}
            applyPeriod={props.applyPeriod}
            refresh={refresh}
            loading={loading}
            activeTab="metrics"
            title={`${host.name || ''} - DBmarlin`}
            description="Host performance detail view, showing key performance metrics and detailed executed statement and waits information"
        >

            {/* Metric Widgets - Host */}
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3">
                <WidgetCard
                    name="CPU Average"
                    type='percent'
                    iconClassName="processor"
                    icon="fa-microchip"
                    value={averageProcessorMetric}
                    previous={previousAverageProcessorMetric ?? undefined}
                    cardTooltipText="The average CPU utilization"
                    loading={loading === DATA_LOADING}
                />
                <WidgetCard
                    name="Memory Average"
                    type='percent'
                    iconClassName="memory"
                    icon="fa-memory"
                    value={averageMemoryMetric}
                    previous={previousAverageMemoryMetric ?? undefined}
                    cardTooltipText="The average memory utilization"
                    loading={loading === DATA_LOADING}
                />
                <WidgetCard
                    name="Disk I/O"
                    type='percent'
                    iconClassName="io"
                    icon="fa-hdd"
                    value={averageDiskMetric ?? 0}
                    previous={previousAverageDiskMetric ?? undefined}
                    cardTooltipText="The average disk I/O utilization"
                    loading={loading === DATA_LOADING}
                />
            </div>

            {/* Charts */}
            <div className="row">
                <div className="col">
                    {Boolean(process.env.REACT_APP_HOST_CHARTS) && loading === DATA_LOADED && (
                        <React.Fragment>
                            <ProcessorChart applyPeriod={props.applyPeriod} host={host} plotLines={chartPlotLines} />
                            <MemoryChart applyPeriod={props.applyPeriod} host={host} plotLines={chartPlotLines} />
                            <DiskChart applyPeriod={props.applyPeriod} host={host} plotLines={chartPlotLines} />
                            <DiskUtilisationChart applyPeriod={props.applyPeriod} host={host} plotLines={chartPlotLines} />
                        </React.Fragment>
                    )}
                </div>
            </div>
        </HostsTabbed>
    );
};

export default Detail;
